import type { SocialSignal, SocialPlatform } from '@/types';

// ---------------------------------------------------------------------------
// Social Signal Intelligence
// 라이브 신호는 Telegram OSINT 스크레이퍼(useDataFetch의 fetchTelegramSignals)가
// 공급한다. 아래 SAMPLE_SIGNALS는 화면 데모용 가상 예시이며 타임스탬프를
// 2026-03-07 기준일로 고정 — 라이브 피드/브레이킹 알럿 경로에 절대 유입 금지.
// ---------------------------------------------------------------------------

// 고정 기준 시각 — 절대 갱신하지 않는다 (가짜 "방금 전" 타임스탬프 금지)
const SAMPLE_REF_ISO = '2026-03-07T12:00:00Z';

const RAW_SAMPLES: Omit<SocialSignal, 'id' | 'timestamp'>[] = [
  // Iran missile situation — Telegram (fastest channel)
  {
    platform: 'telegram',
    text: '🚨 BREAKING: Air sirens activated across Tel Aviv. Iron Dome firing. Unconfirmed reports of Iranian ballistic missiles in flight. #Israel #Iran',
    author: '@OSINTdefender',
    confidence: 78,
    severity: 'critical',
    country: 'Israel',
    countryCode: 'IL',
    lat: 32.1, lon: 34.8,
    keywords: ['sirens', 'Iron Dome', 'missile', 'Iran', 'Israel'],
    category: 'military',
    isFirstReport: true,
    engagementScore: 94500,
  },
  {
    platform: 'twitter',
    text: 'CONFIRMED: IDF Spokesperson confirms missile attack from Iran. 180+ projectiles launched. Arrow 3 and Iron Dome activated nationwide.',
    author: '@IDF_English',
    confidence: 99,
    severity: 'critical',
    country: 'Israel',
    countryCode: 'IL',
    lat: 31.8, lon: 34.8,
    keywords: ['IDF', 'missile', 'Iran', 'Arrow 3', 'Iron Dome'],
    category: 'military',
    isFirstReport: false,
    engagementScore: 320000,
  },
  // Ukraine war signals
  {
    platform: 'telegram',
    text: '⚠️ Multiple explosions reported in Kyiv. Air raid sirens nationwide. Residents urged to shelter immediately. Missile attack underway.',
    author: '@KyivIndependent',
    confidence: 92,
    severity: 'critical',
    country: 'Ukraine',
    countryCode: 'UA',
    lat: 50.4, lon: 30.5,
    keywords: ['Kyiv', 'explosions', 'missile', 'sirens', 'shelter'],
    category: 'conflict',
    isFirstReport: true,
    engagementScore: 56000,
  },
  {
    platform: 'twitter',
    text: 'LIVE: Russia launches over 40 Shahed drones and 8 Kh-101 cruise missiles at Ukraine. Air defense activated in 9 oblasts. #UAWar',
    author: '@UAWeaponsTracker',
    confidence: 88,
    severity: 'critical',
    country: 'Ukraine',
    countryCode: 'UA',
    lat: 48.4, lon: 31.2,
    keywords: ['drones', 'cruise missiles', 'Shahed', 'Ukraine', 'Russia'],
    category: 'military',
    isFirstReport: false,
    engagementScore: 145000,
  },
  // Taiwan Strait tensions
  {
    platform: 'twitter',
    text: '🔴 Taiwan MND confirms 47 PLA aircraft crossed ADIZ median line in past 24hrs. Largest incursion since 2022. Response aircraft scrambled.',
    author: '@TaiwanMND_EN',
    confidence: 96,
    severity: 'high',
    country: 'Taiwan',
    countryCode: 'TW',
    lat: 25.0, lon: 121.5,
    keywords: ['PLA', 'ADIZ', 'Taiwan', 'aircraft', 'incursion'],
    category: 'military',
    isFirstReport: false,
    engagementScore: 89000,
  },
  // Houthi attacks
  {
    platform: 'telegram',
    text: '🚨 FIRST REPORT: Large explosion near tanker vessel in Red Sea, 35nm SW of Aden. Likely anti-ship missile. Crew evacuating. #RedSea #Houthi',
    author: '@AmberyHadfield',
    confidence: 71,
    severity: 'high',
    country: 'Yemen',
    countryCode: 'YE',
    lat: 12.8, lon: 44.9,
    keywords: ['explosion', 'tanker', 'Red Sea', 'missile', 'Houthi'],
    category: 'military',
    isFirstReport: true,
    engagementScore: 34000,
  },
  // North Korea ICBM
  {
    platform: 'twitter',
    text: '🇰🇵 NHK FLASH: Japan government activates J-Alert system. North Korea may have launched ballistic missile. Residents in Hokkaido shelter immediately.',
    author: '@NHK_WORLD_News',
    confidence: 97,
    severity: 'critical',
    country: 'Japan',
    countryCode: 'JP',
    lat: 43.0, lon: 141.3,
    keywords: ['North Korea', 'ICBM', 'J-Alert', 'missile', 'Japan'],
    category: 'military',
    isFirstReport: false,
    engagementScore: 210000,
  },
  // Cyber attack social signal
  {
    platform: 'reddit',
    text: 'BREAKING: NHS hospitals across London completely offline. No patient records accessible. Suspected ransomware. Ambulances being diverted. [r/cybersecurity]',
    author: 'u/CyberSecOps',
    confidence: 74,
    severity: 'high',
    country: 'United Kingdom',
    countryCode: 'GB',
    lat: 51.5, lon: -0.1,
    keywords: ['NHS', 'ransomware', 'offline', 'cyber', 'London'],
    category: 'cyber',
    isFirstReport: true,
    engagementScore: 28000,
  },
  // Earthquake early warning
  {
    platform: 'twitter',
    text: '🔔 USGS: M6.8 earthquake near Kahramanmaraş, Turkey. Felt widely. Possible aftershock zone from 2023 earthquake. Casualty reports incoming.',
    author: '@USGSBigQuakes',
    confidence: 99,
    severity: 'high',
    country: 'Turkey',
    countryCode: 'TR',
    lat: 37.6, lon: 36.9,
    keywords: ['earthquake', 'M6.8', 'Turkey', 'USGS'],
    category: 'earthquake',
    isFirstReport: false,
    engagementScore: 67000,
  },
  // South China Sea
  {
    platform: 'twitter',
    text: 'PHOTOS: Chinese Coast Guard vessel fires water cannons at Philippine resupply boat near Scarborough Shoal. 3 Filipino sailors injured. #SouthChinaSea',
    author: '@PCGOfficial',
    confidence: 90,
    severity: 'high',
    country: 'Philippines',
    countryCode: 'PH',
    lat: 15.1, lon: 117.8,
    keywords: ['South China Sea', 'Coast Guard', 'Philippines', 'Scarborough'],
    category: 'conflict',
    isFirstReport: false,
    engagementScore: 42000,
  },
  // Economic signal
  {
    platform: 'twitter',
    text: 'BREAKING: Fed Chair Powell signals emergency rate decision possible if trade war escalates further. Dollar drops 1.2%. Gold spikes $45.',
    author: '@BloombergBreaking',
    confidence: 85,
    severity: 'high',
    country: 'United States',
    countryCode: 'US',
    lat: 38.9, lon: -77.0,
    keywords: ['Fed', 'rate', 'trade war', 'dollar', 'gold'],
    category: 'economic',
    isFirstReport: false,
    engagementScore: 178000,
  },
  // Sudan conflict
  {
    platform: 'telegram',
    text: '⚠️ First report: RSF forces advancing on Khartoum North. Heavy artillery fire. UN aid convoy blocked. Access corridors for civilians cut off.',
    author: '@SudanWarMonitor',
    confidence: 68,
    severity: 'critical',
    country: 'Sudan',
    countryCode: 'SD',
    lat: 15.6, lon: 32.5,
    keywords: ['Sudan', 'RSF', 'Khartoum', 'artillery', 'civilian'],
    category: 'conflict',
    isFirstReport: true,
    engagementScore: 12000,
  },
  // Weibo signal — China perspective
  {
    platform: 'weibo',
    text: '【重大消息】台湾海峡演习进入实弹阶段。解放军东部战区发布禁航令。 (Major: Taiwan Strait live-fire exercise underway. PLA Eastern Theater issues no-fly zone.)',
    author: '@环球时报',
    confidence: 82,
    severity: 'high',
    country: 'China',
    countryCode: 'CN',
    lat: 26.0, lon: 119.5,
    keywords: ['Taiwan', 'PLA', 'live-fire', 'Eastern Theater', '台湾'],
    category: 'military',
    isFirstReport: false,
    engagementScore: 590000,
  },
  // VK signal — Russian perspective
  {
    platform: 'vk',
    text: 'Крупная победа! Российские войска взяли под контроль Покровск. Украинские силы отступают по всему Донбасскому фронту. (Victory: Russian forces take Pokrovsk control.)',
    author: '@RVvoenkor',
    confidence: 55,
    severity: 'medium',
    country: 'Ukraine',
    countryCode: 'UA',
    lat: 48.3, lon: 37.2,
    keywords: ['Покровск', 'Donbas', 'Russia', 'military', 'front'],
    category: 'conflict',
    isFirstReport: true,
    engagementScore: 89000,
  },
];

// 데모/샘플 전용 — SocialSignalPanel의 SAMPLE 섹션에서만 렌더링.
// id 접두사 'sig-sample-'은 breaking-alert 감지에서 명시적으로 제외된다.
export const SAMPLE_SIGNALS: SocialSignal[] = RAW_SAMPLES.map((s, i) => ({
  ...s,
  id: `sig-sample-${i}`,
  timestamp: SAMPLE_REF_ISO,
}));

// 라이브 피드 — 실제 수집된 신호만 반환한다.
// Telegram OSINT는 useDataFetch에서 별도 fetch되므로 여기서는 빈 배열.
// (과거: 조작 타임스탬프가 붙은 정적 신호를 반환 → FLASH/BREAKING 오경보 유발)
export function fetchSocialSignals(): SocialSignal[] {
  return [];
}

export const PLATFORM_COLORS: Record<SocialPlatform, string> = {
  twitter:  '#1d9bf0',
  telegram: '#229ed9',
  reddit:   '#ff4500',
  weibo:    '#e6162d',
  vk:       '#0077ff',
  signal:   '#3a76f0',
};

export const PLATFORM_ICONS: Record<SocialPlatform, string> = {
  twitter:  '𝕏',
  telegram: '✈',
  reddit:   '🔴',
  weibo:    '微',
  vk:       'В',
  signal:   '🔒',
};
